import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Formik, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Card } from "react-bootstrap";

import { ResidenteService } from "services/ResidenteService";
import { handleErrors } from "jsx/components/CitoPlus/components/ErrorHandlingComponent";
import {
  notifySuccess,
  notifyError,
} from "jsx/components/CitoPlus/components/CitoPlusToastr";
import {
  LinkPrimary,
  LinkDanger,
} from "jsx/components/CitoPlus/components/CitoPlusLink";
import { BotonPrimaryText } from "jsx/components/CitoPlus/components/BotonesCustom";
import { SpinnerCitoPlus } from "jsx/components/CitoPlus/components/loadingLogo";

const validationSchema = Yup.object().shape({
  asunto: Yup.string()
    .required("El asunto es requerido")
    .min(5, "El asunto debe tener al menos 5 caracteres")
    .max(255, "El asunto no puede exceder 255 caracteres"),
  nota: Yup.string()
    .required("La descripción es requerida")
    .min(10, "La descripción debe tener al menos 10 caracteres"),
  fecha_limite: Yup.date().nullable(),
  nombre: Yup.string()
    .max(255, "El nombre no puede exceder 255 caracteres")
    .nullable(),
  cc: Yup.string()
    .max(20, "La cédula no puede exceder 20 caracteres")
    .nullable(),
});

const AutorizacionForm = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const initialValues = {
    asunto: "",
    nota: "",
    fecha_limite: "",
    nombre: "",
    cc: "",
    foto: null,
    documento: null,
  };

  const handleSubmit = async (values, { setSubmitting, resetForm }) => {
    setLoading(true);
    try {
      const dataToSend = {
        asunto: values.asunto,
        nota: values.nota,
        fecha_limite: values.fecha_limite || null,
        nombre: values.nombre || null,
        cc: values.cc || null,
      };

      if (values.foto) {
        dataToSend.foto = values.foto;
      }

      if (values.documento) {
        dataToSend.documento = values.documento;
      }

      await ResidenteService.createAutorizacion(dataToSend);
      notifySuccess("Autorización creada correctamente");
      resetForm();
      navigate("/autorizaciones");
    } catch (error) {
      notifyError("Error al crear la autorización");
      handleErrors(error);
    } finally {
      setLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <>
      <div className="d-flex align-items-center mb-4 flex-wrap">
        <h4 className="fs-20 font-w600 me-auto">Nueva Autorización</h4>
        <LinkPrimary to="/autorizaciones">
          <i className="fas fa-arrow-left me-2"></i>
          Volver
        </LinkPrimary>
      </div>

      <Card>
        <Card.Body>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              setFieldValue,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                <div className="row">
                  <div className="col-lg-12 mb-3">
                    <label className="form-label">
                      Asunto <span className="text-danger">*</span>
                    </label>
                    <Field
                      type="text"
                      name="asunto"
                      className={`form-control ${
                        errors.asunto && touched.asunto ? "is-invalid" : ""
                      }`}
                      placeholder="Ej: Autorización de ingreso para mudanza"
                    />
                    <ErrorMessage
                      name="asunto"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>

                  <div className="col-lg-12 mb-3">
                    <label className="form-label">
                      Descripción <span className="text-danger">*</span>
                    </label>
                    <Field
                      as="textarea"
                      name="nota"
                      rows={4}
                      className={`form-control ${
                        errors.nota && touched.nota ? "is-invalid" : ""
                      }`}
                      placeholder="Describa detalladamente lo que necesita autorizar..."
                    />
                    <ErrorMessage
                      name="nota"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>

                  <div className="col-lg-6 mb-3">
                    <label className="form-label">Fecha Límite</label>
                    <Field
                      type="date"
                      name="fecha_limite"
                      className={`form-control ${
                        errors.fecha_limite && touched.fecha_limite
                          ? "is-invalid"
                          : ""
                      }`}
                    />
                    <ErrorMessage
                      name="fecha_limite"
                      component="div"
                      className="invalid-feedback"
                    />
                    <small className="text-muted">
                      Opcional: fecha límite para la autorización
                    </small>
                  </div>

                  <div className="col-lg-6 mb-3">
                    <label className="form-label">Archivo Adjunto</label>
                    <input
                      type="file"
                      name="foto"
                      className="form-control"
                      accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                      onChange={(event) => {
                        setFieldValue("foto", event.currentTarget.files[0]);
                      }}
                    />
                    <small className="text-muted">
                      Opcional: PDF, DOC, DOCX, JPG, PNG (máx. 5MB)
                    </small>
                  </div>

                  <div className="col-12">
                    <hr className="my-4" />
                    <h5 className="mb-3">
                      <i className="fas fa-user me-2"></i>
                      Datos de la Persona Autorizada (Opcional)
                    </h5>
                  </div>

                  <div className="col-lg-6 mb-3">
                    <label className="form-label">Nombre Completo</label>
                    <Field
                      type="text"
                      name="nombre"
                      className={`form-control ${
                        errors.nombre && touched.nombre ? "is-invalid" : ""
                      }`}
                      placeholder="Nombre de la persona autorizada"
                    />
                    <ErrorMessage
                      name="nombre"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>

                  <div className="col-lg-6 mb-3">
                    <label className="form-label">Cédula / Documento</label>
                    <Field
                      type="text"
                      name="cc"
                      className={`form-control ${
                        errors.cc && touched.cc ? "is-invalid" : ""
                      }`}
                      placeholder="Número de documento"
                    />
                    <ErrorMessage
                      name="cc"
                      component="div"
                      className="invalid-feedback"
                    />
                  </div>

                  <div className="col-lg-6 mb-3">
                    <label className="form-label">
                      Documento de Identidad (Foto)
                    </label>
                    <input
                      type="file"
                      name="documento"
                      className="form-control"
                      accept=".jpg,.jpeg,.png"
                      onChange={(event) => {
                        setFieldValue(
                          "documento",
                          event.currentTarget.files[0],
                        );
                      }}
                    />
                    <small className="text-muted">
                      Opcional: Foto del documento de identidad
                    </small>
                  </div>
                </div>

                <div className="card-footer modal-footer">
                  <LinkDanger to="/autorizaciones">
                    <i className="fas fa-times me-2"></i>
                    Cancelar
                  </LinkDanger>
                  <BotonPrimaryText
                    type="submit"
                    disabled={isSubmitting || loading}
                  >
                    {loading ? (
                      <SpinnerCitoPlus text="Guardando..." />
                    ) : (
                      <>
                        <i className="fas fa-save me-2"></i>
                        Crear Autorización
                      </>
                    )}
                  </BotonPrimaryText>
                </div>
              </form>
            )}
          </Formik>
        </Card.Body>
      </Card>
    </>
  );
};

export default AutorizacionForm;
